import { useEffect, useRef, useState } from "react"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@ui/shad"
import { createPortal } from "react-dom"

import { PlaceholderData } from "@/components/lexical/placeholders"

interface PlaceholderComboboxProps {
  placeholders: PlaceholderData[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (placeholder: PlaceholderData) => void
  anchorRect: DOMRect | null
  placement?: "top" | "bottom"
  portalContainer?: HTMLElement | null
}

export function PlaceholderCombobox({
  placeholders,
  open,
  onOpenChange,
  onSelect,
  anchorRect,
  placement = "bottom",
  portalContainer,
}: PlaceholderComboboxProps) {
  const [search, setSearch] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open && portalContainer) {
      const originalOverflow = portalContainer.style.overflow
      portalContainer.style.overflow = "visible"
      return () => {
        portalContainer.style.overflow = originalOverflow
      }
    }
  }, [open, portalContainer])

  const groupedPlaceholders = placeholders.reduce(
    (acc, placeholder) => {
      if (!acc[placeholder.category]) {
        acc[placeholder.category] = []
      }
      acc[placeholder.category].push(placeholder)
      return acc
    },
    {} as Record<string, PlaceholderData[]>
  )

  useEffect(() => {
    if (open) {
      const timers = [
        setTimeout(() => inputRef.current?.focus(), 0),
        setTimeout(() => inputRef.current?.focus(), 50),
        setTimeout(() => inputRef.current?.focus(), 100),
      ]
      return () => timers.forEach(clearTimeout)
    } else {
      setSearch("")
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let removeListener: (() => void) | null = null

    const timeoutId = setTimeout(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const path = event.composedPath()
        if (containerRef.current && !path.includes(containerRef.current)) {
          onOpenChange(false)
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      removeListener = () => document.removeEventListener("mousedown", handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      removeListener?.()
    }
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        onOpenChange(false)
      }
    }

    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [open, onOpenChange])

  if (!open || !anchorRect) return null

  const getStyle = (): React.CSSProperties => {
    const POPOVER_WIDTH = 280
    const VIEWPORT_PADDING = 16

    if (portalContainer) {
      const containerRect = portalContainer.getBoundingClientRect()
      let relativeLeft = anchorRect.left - containerRect.left
      const relativeTop = anchorRect.top - containerRect.top
      const relativeBottom = anchorRect.bottom - containerRect.top

      const maxLeft = containerRect.width - POPOVER_WIDTH - VIEWPORT_PADDING
      if (relativeLeft > maxLeft) {
        relativeLeft = Math.max(VIEWPORT_PADDING, maxLeft)
      }

      return {
        position: "absolute",
        left: relativeLeft,
        zIndex: 99999,
        ...(placement === "top" ? { bottom: containerRect.height - relativeTop + 4 } : { top: relativeBottom + 4 }),
      }
    }

    let left = anchorRect.left
    const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING
    if (left > maxLeft) {
      left = Math.max(VIEWPORT_PADDING, maxLeft)
    }

    return {
      position: "fixed",
      left,
      zIndex: 99999,
      ...(placement === "top" ? { bottom: window.innerHeight - anchorRect.top + 4 } : { top: anchorRect.bottom + 4 }),
    }
  }

  const style = getStyle()

  return createPortal(
    <div
      ref={containerRef}
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
    >
      <Command
        className="border-border bg-popover w-[280px] rounded-lg border shadow-lg"
        filter={(value, search) => {
          const placeholder = placeholders.find((p) => p.id === value)
          if (!placeholder) return 0
          const searchLower = search.toLowerCase()
          return placeholder.label.toLowerCase().includes(searchLower) ||
            (placeholder.displayValue ?? placeholder.value).toLowerCase().includes(searchLower)
            ? 1
            : 0
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <CommandInput
          ref={inputRef}
          placeholder="Search variables..."
          value={search}
          onValueChange={setSearch}
          autoFocus
        />
        <CommandList>
          <CommandEmpty>No variables found</CommandEmpty>
          {Object.entries(groupedPlaceholders).map(([category, categoryPlaceholders]) => (
            <CommandGroup key={category} heading={category}>
              {categoryPlaceholders.map((placeholder) => (
                <CommandItem
                  key={placeholder.id}
                  value={placeholder.id}
                  onSelect={() => {
                    onSelect(placeholder)
                    onOpenChange(false)
                  }}
                  className="flex w-[280px] justify-between gap-3 truncate"
                >
                  <span className="block w-fit flex-none">{placeholder.label}</span>
                  <span className="text-muted-foreground block max-w-[150px] shrink truncate text-[10px]">
                    {placeholder.displayValue ?? placeholder.value}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </Command>
    </div>,
    portalContainer || document.body
  )
}
